import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { get } from '../lib/api.ts'
import { useCondo } from '../lib/hooks.ts'
import { AgendaFeedButton } from './agenda-feed.tsx'
import { addMonths, daysInMonth, eventsInMonth, eventsOn, KIND_LABEL, localDate, MONTHS, monthGrid, monthKey, pad2, WEEKDAYS, type AgendaEvent, type AgendaKind } from '../lib/agenda.ts'
import { Badge, Button, cn, PageHeader, Sheet, Spinner } from '../components/ui.tsx'
import { LOCALE, lang, t } from '../lib/i18n.ts'

const KIND_BORDER: Record<AgendaKind, string> = { reserva: 'border-caneta', assembleia: 'border-carimbo', prazo: 'border-alerta', mudanca: 'border-margem', prestador: 'border-tinta-3', reforma: 'border-tinta-3' }
const KIND_TONE: Record<AgendaKind, 'alerta' | 'carimbo' | 'margem' | 'neutral'> = { reserva: 'neutral', assembleia: 'carimbo', prazo: 'alerta', mudanca: 'margem', prestador: 'neutral', reforma: 'neutral' }
const KIND_TO: Record<AgendaKind, string> = { reserva: '/c/$slug/reservas', assembleia: '/c/$slug/assembleias', prazo: '/c/$slug/prazos', mudanca: '/c/$slug/portaria', prestador: '/c/$slug/portaria', reforma: '/c/$slug/predio' }
const longDay = (date: string) => {
  const s = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`))
  return s.charAt(0).toUpperCase() + s.slice(1)
}
/** The second line of an item: its hours when it has them, then what the server says about it. */
const detailOf = (e: AgendaEvent) => [e.time && e.endTime ? `${e.time}–${e.endTime}` : null, e.subtitle].filter(Boolean).join(', ')

/** Agenda unificada (issue #11, one server list since ADR 0035): everything with a date, laid out by month. */
export function Agenda() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { condoId } = useCondo(slug)
  const todayStr = localDate(new Date())
  const [ym, setYm] = useState({ y: Number(todayStr.slice(0, 4)), m: Number(todayStr.slice(5, 7)) })
  const [selected, setSelected] = useState(todayStr)
  const first = `${monthKey(ym.y, ym.m)}-01`
  const last = `${monthKey(ym.y, ym.m)}-${pad2(daysInMonth(ym.y, ym.m))}`
  const q = useQuery({
    queryKey: ['agenda', condoId, first, last],
    queryFn: () => get<{ from: string; to: string; today: string; items: AgendaEvent[] }>(`/api/condos/${condoId}/agenda?from=${first}&to=${last}`),
    enabled: Boolean(condoId),
  })
  const events = q.data?.items ?? []
  const grid = monthGrid(ym.y, ym.m)
  const inMonth = eventsInMonth(events, ym.y, ym.m)
  const dayEvents = eventsOn(events, selected)
  const counts = (Object.keys(KIND_LABEL) as AgendaKind[]).map((k) => [k, inMonth.filter((e) => e.kind === k).length] as const).filter(([, n]) => n > 0)
  return (
    <div>
      <PageHeader title={t('Agenda')}>
        <Button variant="quiet" size="sm" aria-label={t('Mês anterior')} onClick={() => setYm(addMonths(ym.y, ym.m, -1))}><ChevronLeft className="size-5" /></Button>
        <span className="min-w-44 text-center font-semibold">{new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(ym.y, ym.m - 1, 15)))}</span>
        <Button variant="quiet" size="sm" aria-label={t('Próximo mês')} onClick={() => setYm(addMonths(ym.y, ym.m, 1))}><ChevronRight className="size-5" /></Button>
        <Button variant="outline" size="sm" onClick={() => { setYm({ y: Number(todayStr.slice(0, 4)), m: Number(todayStr.slice(5, 7)) }); setSelected(todayStr) }}>{t('Hoje')}</Button>
        <AgendaFeedButton condoId={condoId} />
      </PageHeader>
      <p className="mb-4 max-w-prose text-tinta-2">{t('Reservas, assembleias, prazos, mudanças, prestadores e obras aprovadas num só calendário. Toque num dia para ver os detalhes.')}</p>
      {q.isLoading ? <Spinner /> : (
        <Sheet className="overflow-hidden">
          <div className="grid grid-cols-7 border-b border-pauta text-center text-sm text-tinta-2">{WEEKDAYS.map((w) => <div key={w} className="py-2">{w}</div>)}</div>
          <div className="grid grid-cols-7">
            {grid.map((d, i) => {
              if (d === null) return <div key={`x${i}`} className="min-h-14 border-b border-r border-pauta bg-papel/60 sm:min-h-24" aria-hidden />
              const evs = eventsOn(events, d)
              const isToday = d === todayStr
              return (
                <button key={d} type="button" onClick={() => setSelected(d)} aria-pressed={selected === d} aria-label={`${longDay(d)}, ${evs.length} ${evs.length === 1 ? 'item' : 'itens'}`} className={cn('flex min-h-14 flex-col items-start border-b border-r border-pauta p-1 text-left sm:min-h-24', selected === d && 'bg-papel ring-2 ring-inset ring-caneta')}>
                  <span className={cn('inline-block size-6 rounded-full text-center text-sm leading-6', isToday ? 'bg-caneta font-bold text-white' : 'text-tinta')}>{Number(d.slice(8))}</span>
                  <span className="mt-1 flex flex-wrap gap-0.5 sm:w-full sm:flex-col">
                    {evs.slice(0, 3).map((e) => (
                      <span key={e.id} className={cn('block size-1.5 rounded-full border-2 sm:size-auto sm:truncate sm:rounded-none sm:border-0 sm:border-l-2 sm:bg-papel sm:px-1 sm:text-[0.7rem] sm:leading-4', KIND_BORDER[e.kind])}>
                        <span className="hidden sm:inline">{e.time ? `${e.time} ` : ''}{e.title}</span>
                      </span>
                    ))}
                    {evs.length > 3 && <span className="text-[0.7rem] text-tinta-2">+{evs.length - 3}</span>}
                  </span>
                </button>
              )
            })}
          </div>
        </Sheet>
      )}
      <section className="mt-6">
        <h2 className="mb-2 text-xl font-bold">{longDay(selected)}</h2>
        {dayEvents.length === 0 ? <p className="text-tinta-2">{t('Nada marcado neste dia.')}</p> : (
          <Sheet>
            {dayEvents.map((e) => {
              const detail = detailOf(e)
              return (
                <Link key={e.id} to={KIND_TO[e.kind] as '/c/$slug/reservas'} params={{ slug }} className="flex items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0 hover:bg-papel">
                  <Badge tone={KIND_TONE[e.kind]}>{KIND_LABEL[e.kind]}</Badge>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{e.time ? `${e.time}, ` : ''}{e.title}</span>
                    {(detail || e.endDate) && <span className="block text-sm text-tinta-2">{detail}{e.endDate ? `${detail ? ' ' : ''}${t('(até {date})', { date: lang === 'en' ? longDay(e.endDate) : longDay(e.endDate).toLowerCase() })}` : ''}</span>}
                  </span>
                </Link>
              )
            })}
          </Sheet>
        )}
      </section>
      {counts.length > 0 && <p className="mt-4 text-sm text-tinta-2">{t('Neste mês:')}{' '}{counts.map(([k, n]) => `${n} ${KIND_LABEL[k].toLowerCase()}${n > 1 ? 's' : ''}`).join(', ')}.</p>}
    </div>
  )
}
